import React, { useEffect, useState } from 'react';
import { Button, FormGroup, Label, Row, Col, Input, Table, CardFooter, Card } from 'reactstrap';
import { useParams, useNavigate } from 'react-router-dom';
import BreadCrumbs from '../../layouts/breadcrumbs/BreadCrumbs';
import ComponentCard from '../ComponentCard';
import useHttp from '../../hooks/useHttp';
import useAlert from '../../hooks/useAlert';
import AutoComplete from '../common/AutoComplete';
import Loader from '../../layouts/loader/Loader';

const PartnerUserUpdate = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const [zones, setZones] = useState([]);
  const [selectedZone, setSelectedZone] = useState(null);
  const [assignedZones, setAssignedZones] = useState([]);
  const [formData, setFormData] = useState({
    username: '',
    name: '',
    phone: '',
    email: '',
    status: '',
    role: '',
  });

  const { error, isLoading, sendRequest } = useHttp();
  const { alert, successAlert, errorAlert } = useAlert();

  useEffect(() => {
    if (error) errorAlert(error);
  }, [error, errorAlert]);

  useEffect(() => {
    // Fetch user details
    sendRequest(
      {
        method: 'GET',
        url: `${process.env.REACT_APP_IBILL_GATEWAY}/hub/api/v1/partner-user/${username}/details`,
        headers: { Accept: 'application/json' },
      },
      (data) => {
        setFormData({
          username: data.username,
          name: data.name,
          phone: data.phone,
          email: data.email,
          status: data.status,
          role: data.role,
        });
        setAssignedZones(data['zone-accesses'] || []);
      }
    );

    // Fetch zones for autocomplete
    sendRequest(
      {
        method: 'GET',
        url: `${process.env.REACT_APP_IBILL_GATEWAY}/platform/api/v1/zones-for-auto-complete`,
        headers: { Accept: 'application/json' },
      },
      (data) => setZones(data || [])
    );
  }, [sendRequest, username]);

  const handleAssignZone = () => {
    if (selectedZone && !assignedZones.some((zone) => zone['zone-code'] === selectedZone.code)) {
      setAssignedZones((prev) => [...prev, { 'zone-code': selectedZone.code, 'zone-name': selectedZone.name }]);
      setSelectedZone(null);
    }
  };

  const handleRemoveZone = (zoneCode, isDefaultZoneAvailable ) => {

    if (isDefaultZoneAvailable === undefined) {
      setAssignedZones((prev) => prev.filter((zone) => zone['zone-code'] !== zoneCode));
      successAlert('Zone removed successfully.');
    } else {
      sendRequest(
        {
          method: 'DELETE',
          url: `${process.env.REACT_APP_IBILL_GATEWAY}/hub/api/v1/partner-user/${username}/access-zone/${zoneCode}/remove`,
          headers: { Accept: 'application/json' },
        },
        () => {
          setAssignedZones((prev) => prev.filter((zone) => zone['zone-code'] !== zoneCode));
          successAlert('Zone removed successfully.', () => window.location.reload());
        },
      );
    }
  };

  const handleDefaultZone = (zoneCode) => {
    sendRequest(
      {
        method: 'PUT',
        url: `${process.env.REACT_APP_IBILL_GATEWAY}/hub/api/v1/partner-user/${username}/access-zone/${zoneCode}/default`,
        headers: { Accept: 'application/json' },
      },
      () => {
        successAlert('Default zone set successfully.', () => window.location.reload());
      }
    );
  };

  const handleUpdate = () => {
    if(!formData.username || !formData.name || !formData.phone || !formData.email || !formData.status || !formData.role ) {
      errorAlert('Please fill all mandatory fields.');
      return;
    }

    const payload = {
      ...formData,
      'accessible-zones': assignedZones.map((zone) => zone['zone-code']),
    };

    sendRequest(
      {
        method: 'PUT',
        url: `${process.env.REACT_APP_IBILL_GATEWAY}/hub/api/v1/partner-user/${username}/update`,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: payload,
      },
      () => {
        successAlert('User updated successfully.', () => navigate('../list'));
      }
    );
  };

  return (
    <div>
      {alert}
      <BreadCrumbs />
      {isLoading ? (
        <Loader />
      ) : (
        <>
          <ComponentCard title="Update Partner User">
            <Row>
              <Col md={4}>
                <FormGroup>
                  <Label for="name">Name</Label>
                  <Input
                    type="text"
                    name="name"
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label for="phone">Phone</Label>
                  <Input
                    type="text"
                    name="phone"
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label for="email">Email</Label>
                  <Input type="email" name="email" id="email" value={formData.email} disabled />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <FormGroup>
                  <Label for="status">Status</Label>
                  <Input
                    type="select"
                    name="status"
                    id="status"
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <option value="">Select status</option>
                    <option value="ACTIVE">Active</option>
                    <option value="INACTIVE">Inactive</option>
                  </Input>
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label for="username">Username</Label>
                  <Input type="text" name="username" id="username" value={formData.username} disabled />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label for="role">Role <span style={{ color: 'red' }}>*</span></Label>
                  <Input
                    type="select"
                    name="role"
                    id="role"
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  >
                    <option value="">Select role</option>
                    <option value="group-caf-only">CAF Only</option>
                    <option value="group-renewal-topup">Renewal And Topup</option>
                    <option value="group-full-access">Full Access</option>
                  </Input>
                </FormGroup>
              </Col>
            </Row>
          </ComponentCard>
          <ComponentCard title="Zone Access">
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="zoneSearch">Search Zone</Label>
                  <AutoComplete
                    searchItems={zones}
                    handleOnSelect={(item) => setSelectedZone(item)}
                    handleOnClear={() => setSelectedZone(null)}
                  />
                </FormGroup>
              </Col>
              <Col md={2}>
                <Button
                  color="primary"
                  className="mt-4"
                  onClick={handleAssignZone}
                  disabled={!selectedZone}
                >
                  Assign
                </Button>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <h5>Assigned Zones</h5>
                <Table bordered>
                  <thead>
                  <tr>
                    <th>Zone Code</th>
                    <th>Zone Name</th>
                    <th>Actions</th>
                  </tr>
                  </thead>
                  <tbody>
                  {assignedZones.map((zone) => (
                    <tr key={zone['zone-code']}>
                      <td>{zone['zone-code']}</td>
                      <td>{zone['zone-name']}</td>
                      <td>
                        { (zone['default-zone'] === false)  && (
                          <Button
                            color="danger"
                            size="sm"
                            onClick={() => handleRemoveZone(zone['zone-code'], zone['default-zone'])}
                          >
                            Remove
                          </Button>
                        )}
                        { (zone['default-zone'] == null)  && (
                          <Button
                            color="danger"
                            size="sm"
                            onClick={() => handleRemoveZone(zone['zone-code'])}
                          >
                            Remove
                          </Button>
                        )}
                        { (zone['default-zone'] === true)  && (
                          <Button className="btn-outline-warning">
                            Marked as Primary Zone
                          </Button>
                        )}
                        {' '}
                        {zone['default-zone'] !== true && zone['default-zone'] != null && (
                          <Button
                            color="primary"
                            size="sm"
                            onClick={() => handleDefaultZone(zone['zone-code'])}
                          >
                            Set to Primary
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                  </tbody>
                </Table>
              </Col>
            </Row>
          </ComponentCard>
          <Card>
            <CardFooter>
              <Row>
                <div className="col-md-2">
                  <Button
                    className="btn col-12 btn-block btn-danger"
                    onClick={() => navigate('../list')}
                  >
                    Cancel
                  </Button>
                </div>
                <div className="col-md-8" />
                <div className="col-md-2">
                  <Button
                    className="btn col-12 btn-block btn-success"
                    onClick={handleUpdate}
                  >
                    Update
                  </Button>
                </div>
              </Row>
            </CardFooter>
          </Card>
        </>
      )}
    </div>
  );
};

export default PartnerUserUpdate;